import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';

export interface Activity30DaysMonthlyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class Activity30DaysMonthlyDataLoaderFactory extends Factory<
    DataLoader,
    Activity30DaysMonthlyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'Activity30DaysMonthlyDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: Activity30DaysMonthlyDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap;

                // filter from context COMPANY_REVENUE_TYPE and REVENUE_PAYER
                const datasourceColumns = queryContext.datasourceColumns;
                // const datasourceColumns = queryContext.datasourceColumns.filter(
                //     (dsCol) =>
                //         dsCol.id !== 'REVENUE_TYPE'
                // );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    // join,
                } =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [
                                'date >= DATE_TRUNC(DATE_SUB(CURRENT_DATE(), INTERVAL 15 MONTH), MONTH)',
                            ],
                        }
                    );
                const tableFullPath = generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT ${splitSelect}DATE_TRUNC(date, MONTH) AS date
, ROUND(SAFE_DIVIDE(SUM(dau), SUM(stickiness_kpis.last_30d_active_users)), 2) AS stickiness_dau_mau
FROM \`${tableFullPath}\`
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
